// Puerta de RE-ACEPTACIÓN de documentos legales.
//
// Tras autenticar, consulta `GET /me/legal-consent`. Si hay versiones VIGENTES
// que el usuario aún no aceptó (p. ej. porque se publicó una nueva versión de
// los Términos o la Privacidad), BLOQUEA la app y muestra una pantalla que exige
// re-aceptar antes de continuar. Al aceptar, registra el consentimiento (log
// transaccional con método 're_acceptance') y, si no quedan pendientes, deja
// entrar. Si la consulta falla (red/5xx), no bloquea: la app sigue y se
// reintentará en la siguiente sesión (fail-open para no dejar al usuario fuera
// por un problema transitorio del endpoint).

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { useAuth } from './AuthContext.js';
import { AlertMessage, Icon, Spinner } from '../ui/components.js';
import type { MyLegalDocument } from '../api/endpoints.js';

/** Mapea el slug del documento a su ruta pública (abre en pestaña nueva). */
function docPath(slug: string): string {
  return `/legal/${slug}`;
}

export function LegalGate({ children }: { children: ReactNode }) {
  const api = useApi();
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const [checking, setChecking] = useState(true);
  const [pending, setPending] = useState<MyLegalDocument[]>([]);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Consulta inicial de pendientes.
  useEffect(() => {
    let active = true;
    setChecking(true);
    api.me
      .legalConsent()
      .then((res) => {
        if (active) setPending(res.needs_acceptance ? res.pending : []);
      })
      .catch(() => {
        // Fail-open: ante un error no bloqueamos el acceso.
        if (active) setPending([]);
      })
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  const submit = useCallback(async () => {
    if (!accepted || pending.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.me.acceptLegal(pending.map((d) => d.version_id));
      // Si quedaron pendientes (caso raro), reflejarlos; si no, abrir la app.
      setPending(res.needs_acceptance ? res.pending : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('legalGate.error'));
    } finally {
      setBusy(false);
    }
  }, [accepted, pending, api, t]);

  if (checking) return <Spinner label={t('legalGate.checking')} />;

  // Sin pendientes → app normal.
  if (pending.length === 0) return <>{children}</>;

  // Hay pendientes → pantalla de re-aceptación (bloqueante).
  const versionLabel = [...new Set(pending.map((d) => d.version))];
  return (
    <div className="min-h-screen bg-surface-base text-on-surface flex flex-col font-body-md text-body-md">
      <main className="flex-1 w-full max-w-lg mx-auto px-margin-mobile pt-safe pb-20 flex flex-col justify-center gap-space-lg">
        <div className="flex flex-col items-center text-center gap-space-sm">
          <span className="w-16 h-16 rounded-full bg-primary-container/15 border border-primary-container/40 flex items-center justify-center">
            <Icon name="gavel" className="text-primary text-[32px]" />
          </span>
          <h1 className="font-headline-lg text-headline-lg-mobile text-on-surface font-bold">
            {t('legalGate.title')}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">
            {t('legalGate.body')}
          </p>
        </div>

        <div className="surface-card flex flex-col gap-space-md">
          <ul className="flex flex-col gap-space-sm">
            {pending.map((d) => (
              <li
                key={d.version_id}
                className="flex items-center justify-between gap-space-sm p-space-sm rounded-lg bg-surface-deck border border-border-subtle"
              >
                <div className="flex flex-col">
                  <span className="font-body-md text-body-md text-on-surface font-semibold">{d.title}</span>
                  <span className="font-badge-micro text-badge-micro text-on-surface-variant uppercase tracking-wider">
                    {t('legalGate.version', { version: d.version })}
                  </span>
                </div>
                <Link
                  to={docPath(d.slug)}
                  target="_blank"
                  className="font-body-sm text-body-sm text-primary hover:underline flex items-center gap-1 shrink-0"
                >
                  <Icon name="open_in_new" className="text-[16px]" /> {t('legalGate.read')}
                </Link>
              </li>
            ))}
          </ul>

          <AlertMessage message={error} />

          <label
            htmlFor="legalgate-accept"
            className="flex items-start gap-space-xs font-body-sm text-body-sm text-on-surface-variant"
          >
            {/* Casilla sin pre-marcar + acción afirmativa (clickwrap enforceable).
                id + htmlFor explícitos y foco visible (WCAG 2.2 AA). */}
            <input
              type="checkbox"
              id="legalgate-accept"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            />
            <span>
              {versionLabel.length === 1
                ? t('legalGate.acceptWithVersion', { version: versionLabel[0] })
                : t('legalGate.accept')}
            </span>
          </label>

          <button
            type="button"
            className="btn-cyan w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            disabled={!accepted || busy}
            onClick={() => void submit()}
          >
            <Icon name="check_circle" className="text-[20px]" />
            {busy ? t('legalGate.registering') : t('legalGate.submit')}
          </button>

          <button
            type="button"
            onClick={() => void signOut()}
            className="w-full min-h-[44px] py-2.5 px-space-md rounded-lg bg-surface-deck border border-border-subtle text-on-surface-variant font-body-sm text-body-sm hover:text-on-surface transition-colors flex items-center justify-center gap-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <Icon name="logout" className="text-[16px]" /> {t('legalGate.signOut')}
          </button>
        </div>
      </main>
    </div>
  );
}
